/**
 * Input sanitization utilities to prevent XSS and injection attacks.
 */

const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
};

/** Escape HTML special characters to prevent XSS. */
export function escapeHtml(str: string): string {
  if (typeof str !== 'string') return '';
  return str.replace(/[&<>"'/]/g, (char) => HTML_ENTITIES[char] || char);
}

/** Strip javascript:/data:/vbscript:/file:/about: protocols to prevent script-URL XSS; allow only known-safe schemes or relative URLs. */
export function sanitizeUrl(url: string): string {
  if (typeof url !== 'string') return '';

  const trimmedUrl = url.trim().toLowerCase();

  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'about:'];
  if (dangerousProtocols.some(protocol => trimmedUrl.startsWith(protocol))) {
    return '';
  }

  // Allow safe protocols or relative URLs
  if (
    trimmedUrl.startsWith('http://') ||
    trimmedUrl.startsWith('https://') ||
    trimmedUrl.startsWith('/') ||
    trimmedUrl.startsWith('#') ||
    trimmedUrl.startsWith('mailto:') ||
    trimmedUrl.startsWith('tel:')
  ) {
    return url.trim();
  }

  // No protocol = relative URL
  if (!trimmedUrl.includes(':')) {
    return url.trim();
  }

  // Unknown protocol
  return '';
}

/** Sanitize URL query parameters (keys and values escaped, empty keys dropped). */
export function sanitizeQueryParams(
  params: URLSearchParams | Record<string, string>
): Record<string, string> {
  const sanitized: Record<string, string> = {};

  const entries = params instanceof URLSearchParams
    ? Array.from(params.entries())
    : Object.entries(params);

  for (const [key, value] of entries) {
    const safeKey = escapeHtml(key);
    const safeValue = escapeHtml(value);

    if (safeKey) {
      sanitized[safeKey] = safeValue;
    }
  }

  return sanitized;
}

/** Sanitize filename to prevent path traversal attacks. */
export function sanitizeFilename(filename: string): string {
  if (typeof filename !== 'string') return '';

  // Strip path separators, ../ traversal, leading dots
  let safe = filename
    .replace(/\.\./g, '')
    .replace(/[/\\]/g, '')
    .replace(/^\.+/, '')
    .trim();

  // Strip null bytes
  safe = safe.replace(/\0/g, '');

  if (safe.length > 255) {
    safe = safe.substring(0, 255);
  }

  return safe;
}

/**
 * Sanitize user input for display (removes scripts but keeps some formatting).
 * @param trim - false for search inputs so users can type spaces.
 */
export function sanitizeUserInput(input: string, trim: boolean = true): string {
  if (typeof input !== 'string') return '';

  // Strip <script> tags and their content
  let sanitized = input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // Strip inline event handlers (onerror=, onclick=, etc.)
  sanitized = sanitized.replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/\son\w+\s*=\s*[^\s>]*/gi, '');

  // Strip javascript: protocol
  sanitized = sanitized.replace(/javascript:/gi, '');

  // Strip data:text/html (can execute as a document, i.e. XSS)
  sanitized = sanitized.replace(/data:text\/html/gi, '');

  return trim ? sanitized.trim() : sanitized;
}

/** Sanitize email input while typing — strips dangerous patterns but allows partial addresses, no trim/lowercase so typing isn't disrupted. */
export function sanitizeEmail(email: string): string {
  if (typeof email !== 'string') return '';

  let sanitized = email.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  sanitized = sanitized.replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/data:text\/html/gi, '');

  return sanitized;
}

/** Validate and normalize a complete email address (for submission, not while typing). */
export function validateAndNormalizeEmail(email: string): string {
  if (typeof email !== 'string') return '';

  const trimmed = email.trim().toLowerCase();

  const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;

  if (!emailRegex.test(trimmed)) {
    return '';
  }

  return trimmed;
}

/** Sanitize phone number (keep digits, +, -, (, ), spaces only). */
export function sanitizePhone(phone: string): string {
  if (typeof phone !== 'string') return '';

  return phone.replace(/[^0-9+\-() ]/g, '').trim();
}

/**
 * Sanitize alphanumeric input (for usernames, IDs, etc.).
 * @param allowSpaces - default false
 */
export function sanitizeAlphanumeric(input: string, allowSpaces: boolean = false): string {
  if (typeof input !== 'string') return '';

  const regex = allowSpaces ? /[^a-zA-Z0-9 ]/g : /[^a-zA-Z0-9]/g;
  return input.replace(regex, '').trim();
}

/** Sanitize a JSON string by round-tripping it through parse/stringify; returns null if invalid. */
export function sanitizeJsonString(jsonString: string): string | null {
  if (typeof jsonString !== 'string') return null;

  try {
    const parsed = JSON.parse(jsonString);
    return JSON.stringify(parsed);
  } catch (error) {
    return null;
  }
}

/** Remove all HTML tags from a string. */
export function stripHtmlTags(html: string): string {
  if (typeof html !== 'string') return '';
  return html.replace(/<[^>]*>/g, '').trim();
}

/** Sanitize CSS class names (a-z, A-Z, 0-9, -, _ only; can't start with a digit or a hyphen-digit/double-hyphen). */
export function sanitizeClassName(className: string): string {
  if (typeof className !== 'string') return '';

  return className
    .replace(/[^a-zA-Z0-9-_]/g, '')
    .replace(/^[0-9]/, '_')
    .replace(/^--/, '__')
    .trim();
}

/**
 * Recursively sanitize all string values of an object.
 * @param sanitizer - default escapeHtml
 */
export function sanitizeObject<T extends Record<string, any>>(
  obj: T,
  sanitizer: (str: string) => string = escapeHtml
): T {
  const sanitized: any = Array.isArray(obj) ? [] : {};

  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      sanitized[key] = sanitizer(value);
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeObject(value, sanitizer);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized as T;
}
